import {
  Box, Card, CardActionArea, Chip, Grid2, Typography, useTheme,
} from '@mui/material';
import { getCarDetailPageRoute } from '@lib/routing';
import { getCarImages, getPrice, mapCarCategory } from '@plugins/vehiclefleetapp';
import ConsumptionEmissionsNotice, { GRAPHQL_REQUIRED_PROPS_FOR_CONSUMPTION } from '@components/vehiclefleet/ConsumptionEmissionsNotice';
import { DateTime } from 'luxon';
import { FiCheck, FiClock } from 'react-icons/fi';
import { useTranslation } from 'next-i18next';
import { IoCalendarOutline, IoLocationOutline, IoSpeedometerOutline } from 'react-icons/io5';
import { PiGasCanLight } from 'react-icons/pi';
import CarBrandTitle from '../../vehiclefleet/CarBrandTitle';
import Link from '../../atoms/Link';
import { BRAND_LOGO_MAP } from '../../vehiclefleet/InventoryCarListItem';
import Image from '../../atoms/Image';

export default function CarItem(props) {
  const {
    slug,
  } = props;
  const {
    t,
    i18n,
  } = useTranslation();
  const url = getCarDetailPageRoute(slug, i18n.language);

  return (
    <Card
      sx={{
        '& *': {
          textDecoration: 'none !important',
        },
      }}
    >
      <Link href={url}>
        <CardActionArea>
          <CardActionArea>
            <Content t={t} {...props} url={url} />
          </CardActionArea>
        </CardActionArea>
      </Link>
    </Card>
  );
}

function Content(props) {
  const {
    t,
    carAttributes,
    branch,
    brand,
    baselineModel,
    model,
    initialRegistrationDate,
    mileage,
    price,
    fuelType,
    isPluginHybrid,
    isImmediatelyAvailable,
    firstSalesPrice,
    isDemonstrationVehicle,
    isDailyAdmission,
    isVatAvailable,
  } = props;
  const theme = useTheme();
  const modelDetails = (model ?? '')
    .replace(baselineModel, '')
    .trim();
  const images = getCarImages({ attributes: { carAttributes } });
  let carState = 'usedVehicle';

  if (!initialRegistrationDate) {
    carState = 'newVehicle';
  } else if (isDemonstrationVehicle) {
    carState = 'demonstrationCar';
  } else if (isDailyAdmission) {
    carState = 'dailyAdmission';
  }

  return (
    <Grid2 alignItems="stretch" container>
      <Grid2
        size={{
          sm: 3,
          xs: 12,
        }}
        sx={{
          position: 'relative',
          [theme.breakpoints.down('xs')]: {
            marginBottom: theme.spacing(2),
          },
          [theme.breakpoints.up('sm')]: {
            marginRight: theme.spacing(2),
          },
          '& > picture': {
            display: 'block',
          },
        }}
      >
        <Image
          alt={`${brand} ${model}`}
          isFluid
          isSquare
          src={images[0]}
          styles={{
            flex: 1,
            objectFit: 'cover',
            [theme.breakpoints.down('xs')]: {
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
            },
            [theme.breakpoints.up('sm')]: {
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
              display: 'block',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
            },
          }}
        />
      </Grid2>
      <Grid2
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          padding: theme.spacing(3),
        }}
      >
        <Grid2 alignItems="center" container justifyContent="space-between">
          <Grid2 size={{
            md: 'auto',
            xs: 12,
          }}
          >
            <Box alignItems="center" display="flex">
              {BRAND_LOGO_MAP[brand] ? (
                <Image
                  alt={`${brand} Logo`}
                  src={`/images/car-brands/${BRAND_LOGO_MAP[brand]}`}
                  styles={{
                    display: 'block',
                    width: 38,
                    height: 38,
                  }}
                />
              ) : null}
              <Box ml={1}>
                <Typography component="h4" variant="h5">
                  <CarBrandTitle value={brand} />
                  {' '}
                  {baselineModel}
                </Typography>
              </Box>
            </Box>
            <Typography
              component="div"
              variant="body2"
            >
              {modelDetails}
            </Typography>
          </Grid2>
          <Grid2 size={{
            md: 'auto',
            xs: 12,
          }}
          >
            {firstSalesPrice && firstSalesPrice > price && (
              <Box display="flex">
                <Typography
                  component="div"
                  sx={{
                    textDecoration: 'none',
                    position: 'relative',
                    '&::before': {
                      top: '50%', /* tweak this to adjust the vertical position if it's off a bit due to your font family */
                      background: theme.palette.primary.main,
                      opacity: 0.7,
                      content: '""',
                      width: '120%',
                      position: 'absolute',
                      height: '.15em',
                      borderRadius: '.1em',
                      left: '-10%',
                      whiteSpace: 'nowrap',
                      display: 'block',
                      transform: 'rotate(-15deg)',
                    },
                  }}
                  variant="subtitle2"
                >
                  <strong>{getPrice(props, false, 'firstSalesPrice')}</strong>
                </Typography>
              </Box>
            )}
            <Typography component="div">
              <Typography component="span" variant="h5">
                <strong>
                  {getPrice(props)}
                </strong>
              </Typography>
              {isVatAvailable && (
                <Typography component="span" variant="caption">
                  {' '}
                  {t('components.molecules.Search.CarItem.vat')}
                </Typography>
              )}
            </Typography>
          </Grid2>
        </Grid2>
        <Box
          my={1}
          sx={{
            display: 'flex',
          }}
        >
          {isImmediatelyAvailable ? (
            <Box>
              <Chip
                icon={<FiCheck />}
                label={(
                  <span>
                    {t('components.vehiclefleet.InventoryCarListItem.availableImmediately')}
                  </span>
                  )}
                size="small"
                variant="outlined"
              />
            </Box>
          ) : (
            <Box>
              <Chip
                icon={<FiClock />}
                label={(
                  <span>
                    {t('components.vehiclefleet.InventoryCarListItem.canBeOrderedImmediately')}
                  </span>
                  )}
                size="small"
                variant="outlined"
              />
            </Box>
          )}
          <Box ml={1}>
            <Chip
              label={t(`components.vehiclefleet.InventoryCarListItem.vehicleTypes.${carState}`)}
              size="small"
              variant="outlined"
            />
          </Box>
        </Box>
        <Box mb={1} mt={1}>
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 4 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <IoLocationOutline />
                <Typography component="div" ml={0.5} variant="body2">
                  {branch.data?.attributes.name}
                </Typography>
              </Box>
            </Grid2>
            <Grid2 size={{ xs: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <IoSpeedometerOutline />
                <Typography component="div" ml={0.5} variant="body2">
                  {mileage.toLocaleString('de-DE')}
                  {' km'}
                </Typography>
              </Box>
            </Grid2>
            <Grid2 size={{ xs: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <PiGasCanLight />
                <Typography component="div" ml={0.5} variant="body2">
                  {t(`components.vehiclefleet.InventoryCarListItem.fuelTypes.${isPluginHybrid ? 'plugInHybrid' : fuelType}`)}
                </Typography>
              </Box>
            </Grid2>
            {initialRegistrationDate && (
              <Grid2 size={{ xs: 4 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <IoCalendarOutline />
                  <Typography component="div" ml={0.5} variant="body2">
                    {`EZ ${DateTime
                      .fromISO(initialRegistrationDate)
                      .toFormat('MM-yyyy')}`}
                  </Typography>
                </Box>
              </Grid2>
            )}
          </Grid2>
        </Box>
        <Box mb={1}>
          <Box mt={1}>
            <ConsumptionEmissionsNotice
              car={props}
              variant="compact"
            />
          </Box>
        </Box>
      </Grid2>
    </Grid2>
  );
}

CarItem.graphQlProps = `
  id
  attributes {
    search_score
    internalId
    isDeleted
    isPurchased
    slug
    branch {
      data {
        id
        attributes {
          branchId
          name
        }
      }
    }
    category
    brand
    baselineModel
    model
    power
    horsePower
    isNewCar
    generalInspectionDate
    initialRegistrationDate
    mileage
    price
    firstSalesPrice
    recommendedRetailPrice
    currency
    isVatAvailable
    isOldtimer
    isSale
    vehicleIdentificationNumber
    color
    fuelConsumptionCombined
    emissions
    fuelType
    transmissionType
    pollutantClass
    energyEfficiencyClass
    deliveryDate
    isImmediatelyAvailable
    carAttributes
    ${GRAPHQL_REQUIRED_PROPS_FOR_CONSUMPTION}
  }
`;
